import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {Cursor} from '@wonderlandengine/components';
import {Analytics, AnalyticsEvent} from '../classes/Analytics.js';
import {CustomLogTag} from '../classes/Constants.js';
import {Coroutine} from '../classes/coroutines/Coroutine.js';
import {CoroutineManager} from '../classes/coroutines/CoroutineManager.js';
import {
    waitForCondition,
    waitForPromise,
    waitForSeconds,
} from '../classes/coroutines/YieldInstructions.js';
import {Config} from '../config.js';
import {BooleanName} from '../enums/BooleanName.js';
import {GameModeType} from '../enums/GameModeType.js';
import {NotificationLevel} from '../enums/NotificationLevel.js';
import {Sounds} from '../enums/Sounds.js';
import {StatNames} from '../enums/StatNames.js';
import {TransactionalNames} from '../enums/TransactionalNames.js';
import {MenuPanels, UIModel} from '../models/UIModel.js';
import {EventEmitter} from '../utils/EventEmitter.js';
import {Localization} from '../utils/Localization.js';
import {Stack} from '../utils/Stack.js';
import {yesterdayOrBefore} from '../utils/Utils.js';
import {AdManager} from './ad-manager.js';
import {BooleanStore} from './boolean-store.js';
import {CreditsScene} from './credits-scene.js';
import {CurtainsController} from './curtains-controller.js';
import {GameMode} from './game-mode.js';
import {LevelSelect} from './level-select.js';
import {MissionManager} from './mission-manager.js';
import {MusicDirector} from './music-director.js';
import {Puzzle} from './puzzle.js';
import {RewardScene} from './reward-scene.js';
import {Statistics} from './statistics.js';
import {TimeManager} from './time-manager.js';
import {TransitionController} from './transition-controller.js';
import {UnlockManager} from './unlock-manager.js';

/**
 * Type of Phase of a tutorial
 */
export enum TutorialPhaseType {
    Game,
    Interstitial,
    Illustration,
}

/**
 * Description of a tutorial phase
 */
export type TutorialPhase = {
    phaseType: TutorialPhaseType;
    //public GameObject gamePrefab; // TK: not sure how to do this yet 😅
    interstitialText: string[];
    interstitialTitle: string;
};

const TutorialPhases = {
    'long': [
        {
            phaseType: TutorialPhaseType.Interstitial,
            interstitialText: [
                'TutorialPart1Text1',
                'TutorialPart1Text2',
                'TutorialPart1Text3',
            ],
            interstitialTitle: 'TutorialIntro',
        },
        {
            phaseType: TutorialPhaseType.Game,
            interstitialText: [],
            interstitialTitle: 'TutorialDone',
        },
        {
            phaseType: TutorialPhaseType.Interstitial,
            interstitialText: [
                'TutorialPart3Text1',
                'TutorialPart3Text2',
                'TutorialPart3Text3',
            ],
            interstitialTitle: 'TutorialDone',
        },
    ],
    'short': [
        {
            phaseType: TutorialPhaseType.Game,
            interstitialText: [],
            interstitialTitle: 'TutorialDone',
        },
        {
            phaseType: TutorialPhaseType.Interstitial,
            interstitialText: ['TutorialPart3Text1', 'TutorialPart3Text2'],
            interstitialTitle: 'TutorialDone',
        },
    ],
};

/**
 * This component controls the flow of the game and is responsible for showing the different
 * UI elements and starting the game.
 */
export class GameFlowController extends Component {
    static TypeName = 'game-flow-controller';

    private static _instance: GameFlowController;
    static get instance(): GameFlowController {
        return GameFlowController._instance;
    }

    @property.object({required: true})
    levelSelectObject!: Object3D;

    @property.object({required: true})
    uIView!: Object3D;

    @property.object({required: true})
    gameView!: Object3D;

    @property.object({required: true})
    camera!: Object3D;

    @property.object({required: true})
    transitionObject!: Object3D;

    @property.object({required: true})
    normalModePrefab: Object3D;
    @property.object({required: true})
    timedModePrefab: Object3D;
    @property.object({required: true})
    survivalModePrefab: Object3D;
    @property.object({required: true})
    easyModePrefab: Object3D;
    @property.object({required: true})
    zenModePrefab: Object3D;
    @property.object({required: true})
    quickplayModePrefab: Object3D;
    @property.object({required: true})
    fungusModePrefab: Object3D;
    @property.object({required: true})
    cupcakeModePrefab: Object3D;

    // TK: In the original this was a prefab using in the tutorial phases.
    @property.object({required: true})
    tutorialObject!: Object3D;

    @property.object({required: true})
    curtainsControllerObject!: Object3D;

    notificationsAllowed: NotificationLevel = NotificationLevel.Everything;

    get canChangeMenu() {
        return !this._menuTransitionInProgress;
    }

    private _gameMode: GameModeType = GameModeType.Easy;
    // TK: In the original there were 2 properties return the same value (GameMode and GetGameMode), I consolidated.
    get gameMode() {
        return this._gameMode;
    }

    get popupShowing() {
        return this._stackTopIsPopup;
    }

    private _tutorialRunning: boolean = false;
    public get tutorialRunning(): boolean {
        return this._tutorialRunning;
    }

    private _transitionController: TransitionController;
    private _stackTopIsPopup: boolean = false;
    private _puzzle: Puzzle;
    private _levelSelect: LevelSelect;
    private _menuTransitionInProgress = false;
    private _screenStack = new Stack<MenuPanels>();
    private _gameOverReadyDelegates: EventEmitter<GameFlowController> =
        new EventEmitter<GameFlowController>();
    private _tutorialPhaseComplete: boolean;
    private _coroutineManager = new CoroutineManager();
    private _storedGameMode: Object3D;
    private _lastMode: GameModeType = GameModeType.None;
    private _curtainsUI: CurtainsController;

    private _lastGameWasTutorial: boolean = false;

    private _tutorialPhases: TutorialPhase[] = TutorialPhases[Config.tutorialType];

    init(): void {
        if (GameFlowController._instance) {
            this.engine.log.error(
                CustomLogTag.Game,
                'There can only be one instance of GameFlowController Component'
            );
        }
        GameFlowController._instance = this;
    }

    start(): void {
        this._puzzle = Puzzle.instance;

        this._levelSelect = this.levelSelectObject.getComponent(LevelSelect);
        this._curtainsUI = this.curtainsControllerObject.getComponent(CurtainsController);
        MusicDirector.instance.play(Sounds.MusicTitle);

        this._transitionController =
            this.transitionObject.getComponent(TransitionController);

        this.transitionObject.parent = this.camera;

        this._coroutineManager.addCoroutine(new Coroutine(this._startGame()));
    }

    private *_startGame() {
        const tutorialDone = Statistics.instance.getLong(StatNames.TutorialGamesPlayed);
        /* Show the background tiles when the tutorial starts with a panel,
         * or when we don't show the tutorial and go into the menu. */
        if (
            tutorialDone == 0 &&
            this._tutorialPhases[0].phaseType == TutorialPhaseType.Game
        ) {
            this._showBackgroundTiles(false);
            this._transitionController.black();
        } else {
            this._showBackgroundTiles(true);
        }

        yield null;

        yield* waitForCondition(() => Localization.instance.isLoaded);

        const startMenu = MenuPanels.MainMenu;
        this._screenStack.push(startMenu);

        // start the tutorial if necessary
        if (tutorialDone == 0) {
            // first time playing the game, so no ads this time.
            this._lastGameWasTutorial = true;
            Statistics.instance.addAnonymousPurchaseTransaction(
                TransactionalNames.MalwearHeadphones,
                1
            );
            // yield* waitForSeconds(0.5); //tk: Need to give the textures a moment to load in.
            if (this._tutorialPhases[0].phaseType !== TutorialPhaseType.Game) {
                yield* waitForPromise(this._transitionController.irisFromBlack());
            }
            //this._startTutorial();
            yield* this._runTutorial();
        } else {
            UIModel.instance.setMenuActive(startMenu, true);
            yield* waitForSeconds(0.5); //tk: Need to give the textures a moment to load in.
            yield* waitForPromise(this._transitionController.irisFromBlack());
        }
    }

    update(dt: number) {
        this._coroutineManager.update(dt);
    }

    endGame() {
        this.unpause();
        // 	Utils.SetActiveRecursively(malUI, true);
        this._puzzle.resetGame();

        // TK: This is causing the scores to reset on web. I disabled it for now.
        // this._puzzle.score.resetScore();
        this._puzzle.score.resetTime();
        this._puzzle.score.showColumnWarning(false);
        // 	Utils.SetActiveRecursively(puzzle.gameObject, false);
        // 	levelSelectController.HideEnvironment();
        // 	Utils.SetActiveRecursively(gameCamera, false);
        // 	ShowBackgroundTiles(true);
    }

    rewardsActive() {
        // return rewardsScene.gameObject.activeInHierarchy;
        return false;
    }

    /**
     *
     * @param event The mouse event that triggered the start.
     */
    startGameMode(event: MouseEvent | null = null) {
        //if (this._lastMode != GameModeType.None) {
        this._puzzle.resetGame();
        //   	Destroy(lastMode);
        this._lastMode = GameModeType.None;
        //}

        this._coroutineManager.stopCoroutine(this._gameStartSequenceId);
        this._gameStartSequenceId = this._coroutineManager.addCoroutine(
            new Coroutine(this._gameStartSequence(event))
        );

        // this.gameStartSequence().catch((e) => {
        //     this.engine.log.error(CustomLogTag.Game, e);
        // });
    }

    private _gameStartSequenceId: number;
    private *_gameStartSequence(event: MouseEvent | null) {
        this._menuTransitionInProgress = true; // let's treat the game starting as a menu transition.
        /* Special case: When the tutorial starts in the game, we don't want to fade to black
         * as we start out black. We only want to iris-fade out from black */
        if (
            this.gameMode !== GameModeType.Tutorial ||
            this._tutorialPhases[0].phaseType != TutorialPhaseType.Game
        ) {
            yield* waitForPromise(this._transitionController.irisToBlack());
        }

        this.hideMenus();

        // Skip ad if it's the last play was the tutorial.
        if (!this._lastGameWasTutorial && event) {
            // Otherwise, skip ad for the first game of the day
            const lastGame = Statistics.instance.getLong(StatNames.LastGameTimestamp);
            if (lastGame != 0 && !yesterdayOrBefore(lastGame)) {
                yield* waitForPromise(AdManager.instance.showMidgameAd(event));
            }
        }

        this._lastGameWasTutorial = false;

        this._lastMode = this._gameMode;
        yield* waitForPromise(this._levelSelect.load());
        this._storedGameMode.getComponent(GameMode).startGame();

        // waitForCondition(
        //     () => BlobPrefabs.instance.isLoaded && FruitPrefabs.instance.isLoaded
        // );

        // yield return null;

        // levelSelectController.SetEnvironmentIndex(puzzle.Rules.GetStartingEnvironmentIndex());

        this._showBackgroundTiles(false);

        // Utils.SetActiveRecursively(gameCamera, true);

        //  while (!puzzle.GameInProgress)
        //  {
        //  	yield ;
        //  }

        yield* waitForSeconds(0.5); //tk: Need to give the textures a moment to load in.
        yield* waitForPromise(this._transitionController.irisFromBlack());

        // NotificationsAllowed = NotificationLevel.DropDownOnly;

        // while (transitionController.TransitionInProgress)
        // {
        // 	yield return null;
        // }
        this._menuTransitionInProgress = false;
    }

    hideMenus() {
        if (this._screenStack.length > 0) {
            UIModel.instance.hideAll();
            this._screenStack.clear();
            // this._screenStack.peek() = false;
            if (this._stackTopIsPopup) {
                //     Logger.Log('Popping menu ' + screenStack.Peek().name + ' to hide menus.');
                this._screenStack.pop();
                //     Utils.SetActiveRecursively(screenStack.Peek(), false);
            }
            this._stackTopIsPopup = false;
        }
    }

    async pushMenu(
        menu: MenuPanels,
        postEnableDelegate: () => void = null,
        postTransitionDelegate: () => void = null
    ) {
        //    if (!this._menuTransitionInProgress) {
        await this._doPushMenu(menu, true, postEnableDelegate, postTransitionDelegate);
        // .catch(
        // (e) => {
        //     this.engine.log.error(CustomLogTag.Game, e);
        // }
        //);
        //  }
    }

    popMenu(postTransitionDelegate: () => void = null) {
        //Logger.Assert(!menuTransitionInProgress, "Menu can't be pushed while another transition is already in progress!");

        if (!this._menuTransitionInProgress) {
            this._doPopMenu(postTransitionDelegate).catch((e) => {
                this.engine.log.error(CustomLogTag.Game, e);
            });
        }
    }

    isMenuCurrentTop(menu: MenuPanels): boolean {
        let current: MenuPanels;
        if (this._screenStack.length > 0) {
            current = this._screenStack.peek();
        }
        return current == menu;
    }

    gameOver() {
        this.notificationsAllowed = NotificationLevel.None;

        // which screen to show?
        if (
            MissionManager.instance.getMissionsComplete() ||
            MissionManager.instance.getNewMissionsCompleted()
        ) {
            // directly push the game over screen to the stack, so that when missions is closed it
            // goes back to game over.
            this._screenStack.push(MenuPanels.GameOver);

            // Only allow drop-down notifications when going to the Missions screen.
            // This is safe because the player cannot exit the missions screen without going through the
            // rewards screen, which also screws (and eventually resets) notification level.
            this._gameOverSequence(
                MenuPanels.MissionRequirements,
                NotificationLevel.DropDownOnly
            ).catch((e) => {
                this.engine.log.error(CustomLogTag.Game, e);
            });
            // store the notification level
            // const missionsSetNote = this._missionsUI.GetComponentInChildren<SetNotificationLevel>();
            // if (missionsSetNote != null)
            // {
            // 	missionsSetNote.level = NotificationLevel.DropDownOnly;
            // }
            // else
            // {
            // 	Logger.Log("could not find missions Set note!!!");
            // }
        } else {
            this._gameOverSequence(
                MenuPanels.GameOver,
                NotificationLevel.DropDownOnly
            ).catch((e) => {
                this.engine.log.error(CustomLogTag.Game, e);
            });
        }
    }

    goToMainMenu() {
        if (!this._menuTransitionInProgress) {
            this._screenStack.clear();
            UIModel.instance.hideAll();

            this.pushMenu(MenuPanels.MainMenu, () => {
                if (this._lastMode != GameModeType.None) {
                    this.endGame();

                    //Destroy(lastMode);
                    this._lastMode = GameModeType.None;

                    //Puzzle.instance.score.stopCoroutine("StartNewGameReadySetGo");
                }

                // if (topScreen != null)
                // {
                //     Utils.SetActiveRecursively(topScreen, false);
                //     Logger.Log("Popping menu " + topScreen.name + " and all others to return to main menu.");
                // }

                this._stackTopIsPopup = false;
                //Utils.SetActiveRecursively(backgroundUI, true);
                MusicDirector.instance.play(Sounds.MusicTitle);
            });
        }
    }

    goToCredits() {
        if (!this._menuTransitionInProgress) {
            this.notificationsAllowed = NotificationLevel.None;
            this.pushMenu(MenuPanels.Credits, () => {
                CreditsScene.instance.show();
            });
        }
    }

    leaveCredits() {
        if (!this._menuTransitionInProgress) {
            this.notificationsAllowed = NotificationLevel.Everything;
            this.popMenu(() => {
                CreditsScene.instance.hide();
            });
        }
    }

    goToRewards() {
        //	Logger.Assert(!menuTransitionInProgress, "Rewards can't be pushed while another transition is already in progress!");
        if (!this._menuTransitionInProgress) {
            this.notificationsAllowed = NotificationLevel.None;
            this.pushMenu(MenuPanels.Rewards, () => {
                RewardScene.instance.show();
                //this._showBackgroundTiles(false);
                //Utils.SetActiveRecursively(rewardsScene, true);
                //Utils.SetActiveRecursively(curtainsUI.gameObject, true);
                //curtainsUI.Open();
            });
        }
    }

    setMode(gameModeType: GameModeType) {
        switch (gameModeType) {
            case GameModeType.Quickplay:
                this.setQuickplayMode();
                break;
            case GameModeType.Easy:
                this.setEasyMode();
                break;
            //TK: TODO: Add the other modes.
            default:
                console.error('Game Mode not implemented yet');
                break;
        }
    }

    setQuickplayMode() {
        this._storedGameMode = this.quickplayModePrefab;
        this._gameMode = GameModeType.Quickplay;

        // LocalStorage.Save(lastModeKey, gameMode.ToString());
    }

    setEasyMode() {
        this._storedGameMode = this.easyModePrefab;
        this._gameMode = GameModeType.Easy;
        //LocalStorage.Save(lastModeKey, gameMode.ToString());
    }

    registerGameOverScreenReadyCallback(callback: (gfc: GameFlowController) => void) {
        this._gameOverReadyDelegates.add(callback);
    }

    unregisterGameOverScreenReadyCallback(callback: (gfc: GameFlowController) => void) {
        this._gameOverReadyDelegates.remove(callback);
    }

    tutorialPhaseComplete() {
        this._tutorialPhaseComplete = true;
    }

    pause() {
        TimeManager.instance.stopTime();
        //AudioDirector.GetDirector().PauseLoopingSound(true);
    }

    unpause() {
        TimeManager.instance.resumeTime();
        //AudioDirector.GetDirector().PauseLoopingSound(false);
    }

    pushPopupMenu(menu: MenuPanels) {
        if (!this._menuTransitionInProgress) {
            if (this._stackTopIsPopup) {
                //Logger.Error("Can't push a pop-up (" + menu.name + ") on top of an already-showing pop-up (" + screenStack.Peek().name + ")");
            }
            // popups have a special property--they don't deactivate the menu below them
            // in the stack.
            this._pushMenuToStack(menu, false);
        }
    }

    stopTutorial() {
        // QUIT OUT EARLY
        this._tutorialRunning = false;

        // if (PopupShowing)
        // {
        //     PopMenu(null);
        // }

        // Logger.Assert(screenStack.Peek() == tutorialUI, "Some other UI is showing, can't end tutorial!");

        // StopCoroutine("RunTutorial");
        // Unpause();

        // PopMenu(delegate() {
        //     if (Puzzle.GetPuzzle().GameInProgress)
        //     {
        //         EndGame();
        //         QualityControlManager.GetInstance().StopSampling();
        //     }
        //     if (lastMode != null)
        //     {
        //         Destroy(lastMode);
        //         lastMode = null;
        //     }
        //     ShowBackgroundTiles(true);
        //     MusicDirector musicDirector = MusicDirector.GetInstance();
        //     musicDirector.Play(titleMusic);
        //     NotificationsAllowed = NotificationLevel.Everything;
        // });
    }

    isPauseShowing(): boolean {
        return this.isMenuCurrentTop(MenuPanels.Pause);
    }

    goToPauseMenu(postTransitionDelegate: () => void = null) {
        this.pause();
        this.pushMenu(
            MenuPanels.Pause,
            () => {
                // 	Utils.SetActiveRecursively(puzzle.Score.powerupFXParent, false);
                this._showBackgroundTiles(true);
                this._puzzle.score.hideHUDImmediately();
                // 	PowerupManager.GetInstance().HidePowerups();
            },
            postTransitionDelegate
        );
    }

    closePauseMenu() {
        this.unpause();
        this.popMenu(() => {
            // 	HideMenus();
            this._puzzle.score.showHUD();
            this._showBackgroundTiles(false);
            // 	Utils.SetActiveRecursively(Puzzle.GetPuzzle().Score.powerupFXParent, true);
            // 	PowerupManager.GetInstance().ShowPowerups();
        });
    }

    restartGame() {
        // this will cause the top of the stack to be cleared when
        // the transition to the game has hidden it.
        this._stackTopIsPopup = true;
        this._puzzle.score.showColumnWarning(false);
        this.unpause();
        this.startGameMode();
    }

    closeRewards() {
        this._coroutineManager.stopCoroutine(this._endRewardsSequenceId);
        this._endRewardsSequenceId = this._coroutineManager.addCoroutine(
            new Coroutine(this.endRewardsSequence())
        );
    }

    goToCoinStore() {
        this.pushMenu(MenuPanels.StoreCoins, null);
    }

    setEasyModeTryItNow() {
        this.setEasyMode();
        BooleanStore.instance.set(BooleanName.EasyModeClicked, true);
        this.notifySetMode('TryEasyMode');
    }

    notifySetMode(notification) {
        //TK TODO: What do we do with this?
        /** TODO(mccool): Disable temporarily for code freeze.
		GameFlowController flow = GameFlowController.GetInstance();
		flow.PopMenu(delegate() {
			GameFlowController.NotificationLevel previousLevel = flow.NotificationsAllowed;
			flow.NotificationsAllowed = GameFlowController.NotificationLevel.None;
			PopupWindow popup = PopupWindow.GetInstance();
			popup.SetPanel(PopupWindow.PanelStyle.menu);
			flow.PushPopupMenu(popup.panelMenuWindow.gameObject);
			popup.ShowWindow(Localization.instance.Get(notification), 0);
			StartCoroutine(WaitForPopup(popup, previousLevel));
		});
		**/
        UnlockManager.instance.acknowledgedNotification();
    }

    startIngameTutorial() {
        this.pause();
        this._tutorialRunning = true;
        this._stackTopIsPopup = true;
    }
    endIngameTutorial() {
        this.unpause();
        this._tutorialRunning = false;
        this._stackTopIsPopup = false;
    }

    private _endRewardsSequenceId: number;
    private *endRewardsSequence() {
        this._curtainsUI.close();

        while (!this._curtainsUI.curtainsClosed) {
            yield null;
        }

        yield* waitForPromise(this._transitionController.irisToBlack());

        RewardScene.instance.hide();
        this._popMenuFromStack();
        this._showBackgroundTiles(true);

        yield* waitForPromise(this._transitionController.irisFromBlack());

        this.notificationsAllowed = NotificationLevel.Everything;
    }

    static getModeString(mode: GameModeType): string {
        let modeString = Localization.instance.get('AnyMode');
        switch (mode) {
            case GameModeType.Normal:
                modeString = Localization.instance.get('NormalModeLow');
                break;
            case GameModeType.Timed:
                modeString = Localization.instance.get('TimedModeLow');
                break;
            case GameModeType.Survival:
                modeString = Localization.instance.get('SkullModeLow');
                break;
            case GameModeType.Easy:
                modeString = Localization.instance.get('EasyModeLow');
                break;
            case GameModeType.Cupcake:
                modeString = Localization.instance.get('CupcakeModeLow');
                break;
            case GameModeType.Zen:
                modeString = Localization.instance.get('ZenModeLow');
                break;
            case GameModeType.Quickplay:
                modeString = Localization.instance.get('QuickplayModeLow');
                break;
            case GameModeType.Fungus:
                modeString = Localization.instance.get('FungusModeLow');
                break;
            default:
                break;
        }
        return modeString;
    }

    private _runTutorialId: number;
    private _startTutorial() {
        this._coroutineManager.stopCoroutine(this._runTutorialId);
        this._runTutorialId = this._coroutineManager.addCoroutine(
            new Coroutine(this._runTutorial())
        );
    }

    private _playTutorial(modePrefab: Object3D) {
        this._tutorialPhaseComplete = false;
        this._storedGameMode = modePrefab;
        this._gameMode = GameModeType.Tutorial;
        this._levelSelect.changeLevelTutorial();
        this.startGameMode();
    }

    private _setupTutorialUI(phase: TutorialPhase) {
        //interstitialTitleLabel.text = Localization.instance.Get(phase.interstitialTitle);
        // wordBalloon.TextSequence = phase.interstitialText;
    }

    private *_runTutorial() {
        this._tutorialRunning = true;
        Analytics.LogEvent(AnalyticsEvent.TutorialStart);
        this.notificationsAllowed = NotificationLevel.DropDownOnly;

        yield null;

        // Utils.SetActiveRecursively(malUI, false);

        // if (this._tutorialPhases[0].phaseType == TutorialPhaseType.Interstitial)
        //     if (Statistics.instance.getLong(StatNames.TutorialGamesPlayed) == 0) {
        //         {
        // this._setupTutorialUI(this._tutorialPhases[0]);
        this._pushMenuImmediate(MenuPanels.Tutorial, true);
        // 	ShowBackgroundTiles(true);
        //}

        for (let x = 0; x < this._tutorialPhases.length; x++) {
            const currentPhase = this._tutorialPhases[x];
            this._tutorialPhaseComplete = false;
            if (currentPhase.phaseType == TutorialPhaseType.Game) {
                // QualityControlManager.GetInstance().ResetQuality();
                this._playTutorial(this.tutorialObject); //currentPhase.gamePrefab
                while (!this._tutorialPhaseComplete) {
                    yield null;
                }
                if (
                    x + 1 < this._tutorialPhases.length &&
                    this._tutorialPhases[x + 1].phaseType == TutorialPhaseType.Interstitial
                ) {
                    UIModel.instance.setTutorialHUDActive(false);
                    this._gameOverSequence(
                        MenuPanels.Tutorial,
                        NotificationLevel.DropDownOnly
                    ).catch((e) => {
                        this.engine.log.error(CustomLogTag.Game, e);
                    });
                    // 			SetupTutorialUI(tutorialPhases[x + 1]);
                    // 			StartCoroutine(GameOverSequence(tutorialUI, NotificationLevel.DropDownOnly));
                    // 			while (transitionController.TransitionInProgress)
                    // 			{
                    // 				yield return null;
                    // 			}
                    // 			QualityControlManager.GetInstance().SetSampledQuality();
                } else if (x + 1 < this._tutorialPhases.length) {
                    // game -> game?
                } // game -> menu
                else {
                    // 			StartCoroutine(GameOverSequence(gameSetupUI, NotificationLevel.Everything));
                }
            } else if (currentPhase.phaseType == TutorialPhaseType.Interstitial) {
                // interstitial

                // Send data to the tutorial UI
                UIModel.instance.setTutorialPhase(currentPhase);

                while (!this._tutorialPhaseComplete) {
                    yield null;
                }

                if (
                    x + 1 < this._tutorialPhases.length &&
                    this._tutorialPhases[x + 1].phaseType == TutorialPhaseType.Game
                ) {
                    //     // nothing to do in this case.
                } else {
                    if (Statistics.instance.getLong(StatNames.TutorialGamesPlayed) == 0) {
                        Statistics.instance.record(StatNames.TutorialGamesPlayed, 1);
                        Analytics.LogEventWithValue(AnalyticsEvent.GameRank, 0);
                        // 	//cause missions to be set
                        MissionManager.instance.notifyStatsUpdated();
                    }

                    Analytics.LogEventWithValue(AnalyticsEvent.GameRank, 0);

                    // done with the tutorial.  Clean up.
                    if (Config.tutorialSkipMenu) {
                        this.setQuickplayMode();
                        this.startGameMode();
                    } else {
                        this.goToMainMenu();
                        // this.popMenu(() => {
                        //     MusicDirector.instance.play(Sounds.MusicTitle);
                        //     // 				ShowBackgroundTiles(true);
                        this.notificationsAllowed = NotificationLevel.Everything;
                    }
                    // });
                    // 			PopMenu(delegate() {
                    // 				MusicDirector musicDirector = MusicDirector.GetInstance();
                    // 				musicDirector.Play(titleMusic);
                    // 				ShowBackgroundTiles(true);
                    // 				NotificationsAllowed = NotificationLevel.Everything;
                    // 			});
                    //    }
                }
            }
        }
        Analytics.LogEvent(AnalyticsEvent.TutorialDone);
        this._tutorialRunning = false;
    }

    private async _doPushMenu(
        menu: MenuPanels,
        deactivateCurrent: boolean,
        postEnableDelegate: () => void,
        postTransitionDelegate: () => void
    ) {
        this._menuTransitionInProgress = true;

        if (deactivateCurrent) {
            await this._transitionController.fadeToBlack();
        }

        this._pushMenuToStack(menu, deactivateCurrent);

        if (postEnableDelegate != null) {
            postEnableDelegate();
        }

        if (!this._stackTopIsPopup) {
            await this._transitionController.fadeFromBlack();
        }

        this._menuTransitionInProgress = false;

        if (postTransitionDelegate != null) {
            postTransitionDelegate();
        }
    }

    private async _doPopMenu(postTransitionDelegate: () => void) {
        const wasPopup = this._stackTopIsPopup;
        if (!this._stackTopIsPopup) {
            this._menuTransitionInProgress = true;
            await this._transitionController.fadeToBlack();
        }

        this._popMenuFromStack();

        if (postTransitionDelegate != null) {
            postTransitionDelegate();
        }

        if (!wasPopup) {
            await this._transitionController.fadeFromBlack();
            this._menuTransitionInProgress = false;
        }
    }

    private _pushMenuToStack(menu: MenuPanels, deactivateCurrent: boolean) {
        // if the pushed menu is the same as the current top, don't push it again.
        if (!this.isMenuCurrentTop(menu)) {
            if (this._screenStack.length > 0) {
                if (this._stackTopIsPopup) {
                    const current = this._screenStack.pop();
                    UIModel.instance.setPopupActive(false);
                    UIModel.instance.setMenuActive(current, false);
                    this.engine.log.info(
                        CustomLogTag.Game,
                        `Popping menu ${current} to make room for a new popup`
                    );
                }
                //TK: Since popups are not working properly the current menu needs to be deactivated
                if (this._screenStack.length > 0) {
                    // && deactivateCurrent) {
                    UIModel.instance.setMenuActive(this._screenStack.peek(), false);
                }
            }
            // Logger.Log("Pushing menu " + menu.name);
            this._screenStack.push(menu);
            UIModel.instance.setMenuActive(menu, true);
        } else {
            const current = this._screenStack.peek();
            // ensure it's active
            if (UIModel.instance.isMenuActive(current)) {
                // activate again to trigger redraw of UI
                UIModel.instance.setMenuActive(current, true);
            }
        }

        this._stackTopIsPopup = !deactivateCurrent;
        UIModel.instance.setPopupActive(!deactivateCurrent);
    }

    private _popMenuFromStack() {
        if (this._screenStack.length > 0) {
            const current = this._screenStack.pop();
            UIModel.instance.setMenuActive(current, false);
        }

        if (this._screenStack.length > 0) {
            if (!UIModel.instance.isMenuActive(this._screenStack.peek())) {
                UIModel.instance.setMenuActive(this._screenStack.peek(), true);
            }
        } else {
            // fallback!
            //console.error("Screen stack is empty and can't go back!");
            this.pushMenu(MenuPanels.MainMenu);
        }

        this._stackTopIsPopup = false;
        UIModel.instance.setPopupActive(false);
    }

    private async _gameOverSequence(
        nextScreen: MenuPanels,
        newNotificationLevel: NotificationLevel
    ) {
        await this._transitionController.irisToBlack();

        UIModel.instance.setHUDActive(false);
        this._pushMenuImmediate(nextScreen, true);
        // yield return Resources.UnloadUnusedAssets();

        this.endGame();

        this._showBackgroundTiles(true);

        this._gameOverReadyDelegates.emit(this);

        MusicDirector.instance.play(Sounds.MusicTitle);

        await this._transitionController.irisFromBlack();

        // while (transitionController.TransitionInProgress)
        // {
        // 	yield return null;
        // }

        this.notificationsAllowed = newNotificationLevel;
    }

    private _pushMenuImmediate(menu: MenuPanels, deactivateCurrent: boolean) {
        this._pushMenuToStack(menu, deactivateCurrent);
    }

    private _showBackgroundTiles(on: boolean) {
        //TK: the original game just showed and hid the moving blob tiles in the background,
        // in this version the camera moves by reparenting.
        if (on) {
            this.camera.parent = this.uIView;
            this.camera.getComponent(Cursor).active = true;
        } else {
            this.camera.parent = this.gameView;
            this.camera.getComponent(Cursor).active = false;
        }
    }
}
